import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Badge, Box, Button, Group, Loader, Radio, Select, Stack, Text, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconClock, IconUserCheck } from '@tabler/icons-react';
import { userService } from '@/api/services/UserService';
import { useAssignConsignee, useBargainConsignee } from '@/hooks/useDeals';
import { getApiMessage } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import type { CompanyCard } from '@/types/buy';
import type { BargainStepsInfo } from '@/types/deals';
import { formatCompanyLabel, formatOrgName } from '@/lib/format';
import IdText from '@/components/common/IdText';

interface ConsigneeStepProps {
  readonly dealId: number;
  readonly info: BargainStepsInfo;
}

// Шаг «Выбор грузополучателя» (id 3, WAIT_CONSIGNEE_ASSIGNED). Покупатель выбирает грузополучателя
// (по умолчанию — определённый бэком по базису, либо другая организация по ИНН/названию) → POST
// запускает экспедирование. Продавец/агент — ожидают.
export default function ConsigneeStep({ dealId, info }: ConsigneeStepProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isBuyer = info.isBuyer === true;

  const defaultConsignee = useBargainConsignee(dealId, isBuyer);
  const [mode, setMode] = useState<'default' | 'other'>('default');
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [company, setCompany] = useState<CompanyCard | null>(null);
  const assign = useAssignConsignee(dealId);

  // Дебаунс поиска.
  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(input.trim()), 350);
    return () => window.clearTimeout(handle);
  }, [input]);

  // После выбора Mantine подставляет лейбл в строку поиска — по нему не ищем (бэк не поймёт «Имя — ИНН …»).
  const selectedLabel = company?.inn ? formatCompanyLabel(company.name, company.inn, t('auth.fields.inn')) : null;

  const search = useQuery({
    queryKey: ['companySearch', query] as const,
    queryFn: ({ signal }) => userService.searchCompanies(query, signal),
    enabled: isAuthenticated && mode === 'other' && query.length >= 3 && query !== selectedLabel,
    staleTime: 60_000,
  });
  const searching =
    mode === 'other' &&
    ((input.trim().length >= 3 && input.trim() !== query && input.trim() !== selectedLabel) || search.isFetching);

  const pick = useMutation({
    mutationFn: (inn: string) => userService.getCompanyByInn(inn),
    onSuccess: (found) => setCompany(found),
    onError: (error) => notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') }),
  });

  const options = useMemo(() => {
    const items = (search.data ?? [])
      .filter((c) => c.inn)
      .map((c) => ({ value: c.inn!, label: formatCompanyLabel(c.name, c.inn!, t('auth.fields.inn')) }));
    if (company?.inn && selectedLabel && !items.some((o) => o.value === company.inn)) {
      items.unshift({ value: company.inn, label: selectedLabel });
    }
    return items;
  }, [search.data, company, selectedLabel, t]);

  const consigneeInn = mode === 'default' ? defaultConsignee.data?.consigneeInn : company?.inn;
  const canConfirm = !!consigneeInn && !assign.isPending;

  // ── Продавец / Агент — ожидание ──────────────────────────────────────────────
  if (!isBuyer) {
    return (
      <Group gap="xs" wrap="nowrap">
        <ThemeIcon size={32} radius="xl" variant="light" color="brand">
          <IconClock size={18} />
        </ThemeIcon>
        <Text fz="sm">{t('app.deals.consignee.waiting')}</Text>
      </Group>
    );
  }

  // ── Покупатель — выбор ────────────────────────────────────────────────────────
  const def = defaultConsignee.data;
  return (
    <Stack gap="md">
      <Text fz="sm" c="gray.8">
        {t('app.deals.consignee.description')}
      </Text>

      <Radio.Group value={mode} onChange={(value) => setMode(value === 'other' ? 'other' : 'default')}>
        <Stack gap="sm">
          <Radio
            value="default"
            label={
              <Stack gap={2}>
                <Group gap={8} wrap="nowrap">
                  <Text fw={500}>{formatOrgName(def?.consigneeName) ?? t('app.deals.consignee.self')}</Text>
                  <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.7">
                    {t('app.deals.consignee.default')}
                  </Badge>
                </Group>
                {def?.consigneeInn ? (
                  <Text size="sm" c="gray.7">
                    {t('auth.fields.inn')} <IdText>{def.consigneeInn}</IdText>
                    {def.consigneeKpp ? (
                      <>
                        {` · ${t('app.company.card.kpp')} `}
                        <IdText>{def.consigneeKpp}</IdText>
                      </>
                    ) : null}
                  </Text>
                ) : null}
              </Stack>
            }
          />
          <Radio value="other" label={<Text fw={500}>{t('app.deals.consignee.other')}</Text>} />
        </Stack>
      </Radio.Group>

      {mode === 'other' ? (
        <Stack gap="sm" pl={36}>
          <Select
            placeholder={t('app.deals.consignee.search')}
            searchable
            clearable
            data={options}
            filter={({ options: opts }) => opts}
            searchValue={input}
            onSearchChange={setInput}
            value={company?.inn ?? null}
            onChange={(inn) => (inn ? pick.mutate(inn) : setCompany(null))}
            nothingFoundMessage={
              query.length < 3 ? t('app.deals.consignee.typeMore') : searching ? t('common.searching') : t('app.buy.nothingFound')
            }
            rightSection={searching || pick.isPending ? <Loader size={16} /> : undefined}
            disabled={pick.isPending}
            maw={560}
          />
          {company ? (
            <Box>
              <Text fz="sm" fw={500}>
                {formatOrgName(company.name) ?? '—'}
              </Text>
              <Text fz="sm" c="gray.7">
                {t('auth.fields.inn')} <IdText>{company.inn}</IdText>
                {company.kpp ? (
                  <>
                    {` · ${t('app.company.card.kpp')} `}
                    <IdText>{company.kpp}</IdText>
                  </>
                ) : null}
                {company.address ? ` · ${company.address}` : ''}
              </Text>
            </Box>
          ) : null}
        </Stack>
      ) : null}

      <Button
        leftSection={<IconUserCheck size={16} />}
        disabled={!canConfirm}
        loading={assign.isPending}
        onClick={() => assign.mutate(consigneeInn!)}
        w="fit-content"
      >
        {t('app.deals.consignee.confirm')}
      </Button>
    </Stack>
  );
}
